import React from 'react';
import PropTypes from 'prop-types';
import { Link as RouterLink } from 'react-router-dom';
import {
  Button,
  Label,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@tempmee/react';
import { TableCard } from '@tempmee/react-blocks/experimental';
import { getMembershipStatusSeverity } from '../../../../domain/Awards';

// The last column is named for what it is rather than for the one control it
// holds today, so adding a second row action later needs no rename.
const COLUMNS = ['Name', 'User Type', 'Status', 'Application', 'Feedback', 'Actions'];

// Absence is a muted en dash — at body colour a column of dashes reads as
// content rather than absence.
const NotAvailable = () => <span className="text-text-secondary">–</span>;

/**
 * "View" opens the record; a dash means there is nothing to open (GOTU-7392,
 * on awards-service GOTU-7230's two admin reads). Reading an application or a
 * review used to mean exporting the whole cycle as a CSV and searching by hand.
 *
 * A word rather than the green check this column used to show. The check was a
 * *status* glyph — it reads "yes", not "activate me" — so making it the control
 * conflated state with action and left the affordance to be guessed at. The
 * presence signal survives intact: a row either offers View or shows a dash.
 * This also restores what GOTU-4818 originally shipped here, which was dropped
 * in GOTU-5889 only because the endpoints behind it did not exist yet.
 *
 * A text Button rather than a Link, because this opens a dialog rather than
 * navigating — the same control ExportLink next door uses for the same reason.
 * The accessible name names the member, since a roster is many identical
 * "View"s; it opens with the visible word so the two agree (WCAG 2.5.3).
 */
const PresenceCell = ({ present, label, onOpen }) =>
  (present ? (
    <Button variant="text" size="small" onClick={onOpen} aria-label={label}>
      View
    </Button>
  ) : (
    <NotAvailable />
  ));

PresenceCell.propTypes = {
  present: PropTypes.bool.isRequired,
  /**
   * Accessible name, naming the member. Must begin with the visible "View" so
   * the accessible name contains the visible label (WCAG 2.5.3).
   */
  label: PropTypes.string.isRequired,
  onOpen: PropTypes.func.isRequired,
};

/**
 * The cycle roster. The filters ride in TableCard's toolbar slot.
 *
 * The error branch is ours: TableCard has no failure state, and "no users yet"
 * must not stand in for a request that failed.
 */
export const MembershipTable = ({
  memberships,
  isPending,
  isError,
  isEditable,
  renderStatus,
  toolbar,
  footer,
  emptyMessage,
  onOpenApplication,
  onOpenFeedback,
}) => {
  if (isError) {
    return (
      <TableCard title="Users" toolbar={toolbar}>
        <Typography className="text-error">Could not load users.</Typography>
      </TableCard>
    );
  }

  return (
    <TableCard
      title="Users"
      toolbar={toolbar}
      footer={footer}
      loading={isPending}
      empty={!isPending && memberships.length === 0}
      emptyMessage={emptyMessage}
    >
      <TableContainer>
        <Table size="medium">
          <TableHead>
            <TableRow>
              {COLUMNS.map((column) => (
                <TableCell key={column}>{column}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
          {memberships.map((row) => (
            <TableRow key={row.userId} hover>
              <TableCell>{row.displayName}</TableCell>
              <TableCell>{row.userType}</TableCell>
              <TableCell>
                {isEditable ? (
                  renderStatus(row)
                ) : (
                  <Label severity={getMembershipStatusSeverity(row.status)} variant="standard">
                    {row.statusLabel}
                  </Label>
                )}
              </TableCell>
              <TableCell>
                {/* Named for the person, not the column: a screen reader
                    reaching a column of identical "View"s has no way to tell
                    which member each one belongs to. */}
                <PresenceCell
                  present={row.hasApplication}
                  label={`View ${row.displayName}'s application`}
                  onOpen={() => onOpenApplication(row)}
                />
              </TableCell>
              <TableCell>
                <PresenceCell
                  present={row.hasFeedback}
                  label={`View feedback for ${row.displayName}`}
                  onOpen={() => onOpenFeedback(row)}
                />
              </TableCell>
              <TableCell>
                {/* ADM rows have no domain profile page. */}
                {row.profilePath ? (
                  <RouterLink to={row.profilePath}>View Profile</RouterLink>
                ) : (
                  <NotAvailable />
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
        </Table>
      </TableContainer>
    </TableCard>
  );
};

MembershipTable.propTypes = {
  memberships: PropTypes.arrayOf(
    PropTypes.shape({
      userId: PropTypes.string.isRequired,
      displayName: PropTypes.string.isRequired,
      userType: PropTypes.string.isRequired,
      statusLabel: PropTypes.string.isRequired,
      hasApplication: PropTypes.bool.isRequired,
      hasFeedback: PropTypes.bool.isRequired,
      profilePath: PropTypes.string,
    }),
  ).isRequired,
  isPending: PropTypes.bool.isRequired,
  isError: PropTypes.bool.isRequired,
  isEditable: PropTypes.bool.isRequired,
  /** Renders the editable status control for a row. Ignored when not editable. */
  renderStatus: PropTypes.func.isRequired,
  /** Opens the member's submitted application. Receives the row. */
  onOpenApplication: PropTypes.func.isRequired,
  /** Opens the reviews the member received. Receives the row. */
  onOpenFeedback: PropTypes.func.isRequired,
  /** Filter bar, rendered in the card's toolbar slot. */
  toolbar: PropTypes.node,
  /** Footer slot — the page passes its pagination. */
  footer: PropTypes.node,
  /**
   * Shown when the roster is empty. The page varies it so a filtered-to-zero
   * result reads differently from a genuinely empty cycle (GOTU-6400).
   */
  emptyMessage: PropTypes.string,
};

MembershipTable.defaultProps = {
  toolbar: null,
  footer: null,
  emptyMessage: 'No users in this cycle yet.',
};
